import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getDashboardPath } from "../../../utils/authRedirect";
import { PORTALS } from "../../../utils/portalTypes";
import {
  hasAdminAccess,
  isClientRole,
  isTrainerRole,
  normalizeRole,
} from "../../../utils/roles";
import clinicAPI from "../../../services/clinicApi";
import L from "../../../constants/baysportLabels";
import LandingNav from "../HomeLanding/LandingNav";
import logo from "../../../assets/logo.svg";
import s from "../HomeLanding/homeLanding.module.css";

function userMatchesPortal(role, portal) {
  const r = normalizeRole(role);
  if (portal.id === "admin") return hasAdminAccess(r);
  if (portal.id === "staff") return isTrainerRole(r);
  if (portal.id === "patient") return isClientRole(r);
  return r === normalizeRole(portal.role);
}

const PORTAL_SECTIONS = [
  {
    portal: PORTALS.patient,
    icon: "🏃",
    title: L.portalClient,
    desc: "Расписание, абонемент, оплаты и клубная лента — всё в телефоне.",
    featured: true,
    actions: (loggedIn, role) => {
      if (loggedIn && userMatchesPortal(role, PORTALS.patient)) {
        return [{ label: "Открыть кабинет", to: getDashboardPath(role), primary: true }];
      }
      return [
        { label: "Записаться", to: "/book", primary: true },
        { label: "Войти", to: PORTALS.patient.login },
        { label: "Регистрация", to: PORTALS.patient.register, ghost: true },
      ];
    },
  },
  {
    portal: PORTALS.staff,
    icon: "📋",
    title: L.portalTrainer,
    desc: "Журнал посещаемости, расписание групп и карточки спортсменов.",
    actions: (loggedIn, role) => {
      if (loggedIn && userMatchesPortal(role, PORTALS.staff)) {
        return [{ label: "Открыть кабинет", to: getDashboardPath(role), primary: true }];
      }
      return [{ label: "Войти", to: PORTALS.staff.login, primary: true }];
    },
  },
  {
    portal: PORTALS.admin,
    icon: "⚙️",
    title: L.portalAdmin,
    desc: "Клиенты, финансы, абонементы, команда и настройки клуба.",
    actions: (loggedIn, role) => {
      if (loggedIn && userMatchesPortal(role, PORTALS.admin)) {
        return [{ label: "Открыть кабинет", to: getDashboardPath(role), primary: true }];
      }
      return [{ label: "Войти", to: PORTALS.admin.login, primary: true }];
    },
  },
];

const CLUB_FEATURES = [
  {
    icon: "📅",
    title: "Расписание",
    text: "Групповые и индивидуальные занятия — всегда под рукой.",
  },
  {
    icon: "🎫",
    title: "Абонементы",
    text: "Статус, оплаты и остаток занятий в личном кабинете.",
  },
  {
    icon: "📱",
    title: "PWA на телефоне",
    text: "Установите кабинет как приложение — без App Store.",
  },
];

function resolveFileUrl(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("/api/")) return url;
  return `/api/files/${url.replace(/^\/+/, "")}`;
}

export default function PortalLanding() {
  const { isAuth, role } = useSelector((state) => state.auth);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clinicAPI
      .getPublicClinicPortal()
      .then((data) => setClinic(data?.clinic || data?.tenant || data))
      .catch(() => setClinic(null))
      .finally(() => setLoading(false));
  }, []);

  const roleLabels = {
    owner: "владелец",
    admin: "администратор",
    trainer: "тренер",
    client: "клиент",
  };
  const roleLabel = roleLabels[normalizeRole(role)] || normalizeRole(role);

  const clubName = clinic?.name || clinic?.title || L.club;
  const clubDesc =
    clinic?.portal_description ||
    "Онлайн-запись, личные кабинеты и удобное управление занятиями.";
  const coverUrl = resolveFileUrl(clinic?.portal_cover_url);

  return (
    <div className={`${s.page} ${s.pageLanding}`}>
      {isAuth ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли как <strong>{roleLabel}</strong>
          </span>
          <div className={s.sessionActions}>
            <Link to={getDashboardPath(role)} className={`${s.btn} ${s.btnPrimary}`}>
              Открыть кабинет
            </Link>
          </div>
        </div>
      ) : null}

      <LandingNav
        mode="club"
        brandName={loading ? L.productName : clubName}
        showPortalSwitcher
      />

      <section className={s.hero}>
        <div>
          <div className={s.eyebrow}>
            {L.productName} · {clubName}
          </div>
          <h1 className={s.title}>
            Добро пожаловать
            <br />
            <span className={s.titleAccent}>в {clubName}</span>
          </h1>
          <p className={s.lead}>{clubDesc}</p>
          <div className={s.heroActions}>
            <Link to="/book" className={`${s.btn} ${s.btnPrimary}`}>
              Записаться на занятие
            </Link>
            <Link to={PORTALS.patient.login} className={`${s.btn} ${s.btnOutline}`}>
              Войти в кабинет
            </Link>
          </div>
          <div className={s.metrics}>
            <div className={s.metric}>
              <strong>Клиентам</strong>
              <span>запись и кабинет</span>
            </div>
            <div className={s.metric}>
              <strong>Тренерам</strong>
              <span>журнал и группы</span>
            </div>
            <div className={s.metric}>
              <strong>Админам</strong>
              <span>управление клубом</span>
            </div>
          </div>
        </div>

        <div className={s.clubHeroCover}>
          {coverUrl ? (
            <img src={coverUrl} alt="" />
          ) : (
            <div className={s.clubHeroCoverPlaceholder}>
              <img
                src={logo}
                alt=""
                style={{ height: 32, marginBottom: 12, opacity: 0.9 }}
              />
              <strong>{clubName}</strong>
              <p>Спортивный клуб на платформе {L.productName}</p>
            </div>
          )}
        </div>
      </section>

      <section id="portals" className={`${s.section} ${s.sectionAlt}`}>
        <h2 className={s.sectionTitle}>Вход в систему</h2>
        <p className={s.sectionLead}>
          Выберите свой портал — для спортсменов, тренеров или администраторов
          клуба.
        </p>
        <div className={s.portalGrid}>
          {PORTAL_SECTIONS.map(({ portal, icon, title, desc, featured, actions }) => {
            const actionList = actions(isAuth, role);
            return (
              <article
                key={portal.id}
                className={`${s.portalCard} ${featured ? s.portalCardFeatured : ""}`}
              >
                <div className={s.portalCardHead}>
                  <span className={s.portalCardIcon} aria-hidden>
                    {icon}
                  </span>
                  <div>
                    <h3 className={s.portalCardTitle}>{title}</h3>
                    <p className={s.portalCardDesc}>{desc}</p>
                  </div>
                </div>
                <div className={s.portalCardActions}>
                  {actionList.map((action) => (
                    <Link
                      key={action.label}
                      to={action.to}
                      className={`${s.btn} ${
                        action.primary
                          ? s.btnPrimary
                          : action.ghost
                            ? s.btnGhost
                            : s.btnOutline
                      }`}
                    >
                      {action.label}
                    </Link>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Для спортсменов и родителей</h2>
        <p className={s.sectionLead}>
          Всё необходимое для занятий — в одном личном кабинете.
        </p>
        <div className={s.features}>
          {CLUB_FEATURES.map((f) => (
            <article key={f.title} className={s.feature}>
              <div className={s.featureIcon}>{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={s.cta}>
        <div className={s.ctaBox}>
          <h2 className={s.sectionTitle} style={{ marginBottom: 12 }}>
            Нужна запись на занятие?
          </h2>
          <p className={s.sectionLead} style={{ margin: "0 auto 24px" }}>
            Выберите направление, тренера и удобное время — без звонков и
            ожидания.
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Link to="/book" className={`${s.btn} ${s.btnPrimary}`}>
              Записаться онлайн
            </Link>
            <Link to={PORTALS.patient.register} className={`${s.btn} ${s.btnOutline}`}>
              Создать кабинет
            </Link>
          </div>
        </div>
      </section>

      <footer className={s.footer}>
        © {new Date().getFullYear()} {clubName} · {L.productName}
      </footer>
    </div>
  );
}
